import React from 'react'
import { Button, SxProp } from 'theme-ui'

import Icon from 'src/components/gel/Icon'
import Type from 'src/components/gel/Type'
import { isDarkBackground, isLightBackground } from 'src/lib/styling'

import type { IconVariant } from 'src/components/gel/Icon'
import type { ButtonVariants } from 'src/theme/adSales/buttons'
import type { BackgroundColorTypes } from 'src/theme/adSales/colors'

type BaseButtonProps = {
  leftIcon?: IconVariant
  text?: string
  rightIcon?: IconVariant
  variant?: ButtonVariants
  onClick?: (e: React.MouseEvent<HTMLButtonElement>, text?: string) => void
  disabled?: boolean
  sx?: SxProp['sx']
  backgroundColor?: BackgroundColorTypes
  backgroundMatch?: boolean
  iconDimensions?: { width: string; height: string; verticalAlign?: string; sx?: SxProp['sx'] }
  name?: string
  className?: string
  link?: string
  openNewTab?: boolean
}

const BaseButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
  (
    {
      leftIcon,
      text,
      rightIcon,
      variant = 'primary',
      onClick,
      disabled,
      sx,
      backgroundColor = 'background.quaternary',
      backgroundMatch = false,
      iconDimensions,
      name,
      className,
      link,
      openNewTab = false
    },
    ref
  ) => {
    const iconProps = {
      width: iconDimensions?.width || '20px',
      height: iconDimensions?.height || '20px'
    }

    const buttonVariant = variant.includes('navigation')
      ? variant
      : `${variant}${
          backgroundMatch
            ? isLightBackground(backgroundColor)
              ? 'Dark'
              : ''
            : isDarkBackground(backgroundColor)
            ? 'Dark'
            : ''
        }`

    return (
      <Button
        variant={buttonVariant}
        onClick={e => onClick && onClick(e, text)}
        disabled={disabled}
        sx={sx as any}
        ref={ref}
        aria-label={name}
        className={className}
        as={link ? 'a' : 'button'}
        {...(link && { href: link })}
        {...(openNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {leftIcon && (
          <Icon
            variant={leftIcon}
            sx={{
              mr: text ? 2 : 0,
              verticalAlign: iconDimensions?.verticalAlign || 'middle',
              display: 'inline-flex',
              ...iconDimensions?.sx
            }}
            {...iconProps}
          />
        )}
        {text && (
          <Type variant="Labels" as="span">
            {text}
          </Type>
        )}
        {rightIcon && (
          <Icon
            variant={rightIcon}
            sx={{
              ml: text ? 2 : 0,
              alignContent: 'center',
              verticalAlign: iconDimensions?.verticalAlign || 'middle',
              display: 'inline-flex',
              ...iconDimensions?.sx
            }}
            {...iconProps}
          />
        )}
      </Button>
    )
  }
)

BaseButton.displayName = 'BaseButton'
export default BaseButton
